import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api, type LocalizedText } from '../../api.ts';
import { FormModal, FormSection } from '../../components/FormModal.tsx';
import { ListToolbar } from '../../components/ListFilters.tsx';
import { MasterDeleteButton } from '../../components/MasterDelete.tsx';
import { ConfirmDialog } from '../../components/Modal.tsx';
import {
  Badge,
  Button,
  EmptyRow,
  Field,
  Input,
  issueFor,
  Select,
  Table,
} from '../../components/ui.tsx';
import { localized } from '../../i18n/index.ts';

export type PlNodeKind = 'group' | 'subtotal' | 'indicator';
export interface PlNode {
  id: string;
  parentId: string | null;
  code: string;
  name: LocalizedText;
  kind: PlNodeKind;
  block: 'result' | 'outside';
  numeratorId: string | null;
  denominatorId: string | null;
  sortOrder: number;
  lineIds: string[];
  termIds: string[];
}
interface SectionLike {
  id: string;
  code: string;
  expenseTypeName: string;
  block: 'result' | 'outside';
  isSubtotal: boolean;
}
interface LineLike {
  id: string;
  sectionId: string;
  code: string;
  classificationName: string;
}
type NodeForm = Omit<PlNode, 'id'>;

const KIND_TONE = { group: 'neutral', subtotal: 'info', indicator: 'accent' } as const;

/** Ordem de apresentação: cada item seguido dos filhos, com a profundidade. */
export function orderedNodes(nodes: readonly PlNode[]) {
  const out: { node: PlNode; depth: number }[] = [];
  const walk = (parentId: string | null, depth: number) => {
    for (const n of nodes
      .filter((x) => x.parentId === parentId)
      .sort((a, b) => a.sortOrder - b.sortOrder || a.code.localeCompare(b.code))) {
      out.push({ node: n, depth });
      walk(n.id, depth + 1);
    }
  };
  walk(null, 0);
  return out;
}

/**
 * Estrutura da DRE em níveis (ADR 0049, PR-7b): grupos de linhas XX.XX, subtotais e indicadores
 * (margens), configurados no mapeamento em rascunho. Nada é fixo no código: a DRE mostra o que
 * estiver aqui.
 */
export function PlStructureTab({
  mappingId,
  nodes,
  sections,
  lines,
  manage,
  onChanged,
}: {
  mappingId: string;
  nodes: PlNode[];
  sections: SectionLike[];
  lines: LineLike[];
  manage: boolean;
  onChanged: () => Promise<unknown>;
}) {
  const { t } = useTranslation();
  const [edit, setEdit] = useState<{ id: string | null; form: NodeForm } | null>(null);
  const [removing, setRemoving] = useState<string | null>(null);
  const byId = new Map(nodes.map((n) => [n.id, n]));
  const lineById = new Map(lines.map((l) => [l.id, l]));
  const save = useMutation({
    mutationFn: ({ id, form }: NonNullable<typeof edit>) => {
      const body = {
        kind: form.kind,
        code: form.code,
        name: { 'pt-BR': form.name['pt-BR'], en: form.name.en || form.name['pt-BR'] },
        block: form.kind === 'group' ? form.block : 'result',
        parentId: form.kind === 'group' ? form.parentId : null,
        numeratorId: form.kind === 'indicator' ? form.numeratorId : null,
        denominatorId: form.kind === 'indicator' ? form.denominatorId : null,
        sortOrder: form.sortOrder,
        lineIds: form.kind === 'group' ? form.lineIds : [],
        termIds: form.kind === 'subtotal' ? form.termIds : [],
      };
      return id
        ? api.put(`/fpa/pl/nodes/${id}`, body)
        : api.post('/fpa/pl/nodes', { ...body, mappingVersionId: mappingId });
    },
    onSuccess: async () => {
      setEdit(null);
      await onChanged();
    },
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/fpa/pl/nodes/${id}`),
    onSuccess: async () => {
      setRemoving(null);
      await onChanged();
    },
  });
  const f = edit?.form;
  const set = (patch: Partial<NodeForm>) => edit && setEdit({ ...edit, form: { ...f!, ...patch } });
  const toggle = (list: string[], id: string) =>
    list.includes(id) ? list.filter((x) => x !== id) : [...list, id];
  // Linha já usada em outro grupo: não pode entrar em dois (sem dupla contagem).
  const usedBy = new Map(
    nodes.filter((n) => n.id !== edit?.id).flatMap((n) => n.lineIds.map((l) => [l, n] as const)),
  );
  // Grupo com muitas linhas: a quantidade e as primeiras (a lista completa fica na edição).
  const lineCodes = (n: PlNode) => {
    const codes = n.lineIds
      .map((l) => lineById.get(l)?.code)
      .filter((c): c is string => !!c)
      .sort();
    if (!codes.length) return '—';
    return codes.length <= 6
      ? codes.join(', ')
      : t('fpaPl.structure.lineCount', {
          count: codes.length,
          first: codes.slice(0, 4).join(', '),
        });
  };
  const composition = (n: PlNode) =>
    n.kind === 'group'
      ? lineCodes(n)
      : n.kind === 'subtotal'
        ? n.termIds.map((x) => byId.get(x)?.code ?? '?').join(' + ')
        : `${byId.get(n.numeratorId!)?.code ?? '?'} ÷ ${byId.get(n.denominatorId!)?.code ?? '?'}`;
  const resultNodes = nodes.filter(
    (n) => n.kind !== 'indicator' && n.block === 'result' && n.id !== edit?.id,
  );

  return (
    <>
      <ListToolbar
        action={
          manage && (
            <Button
              onClick={() => {
                save.reset();
                setEdit({
                  id: null,
                  form: {
                    parentId: null,
                    code: '',
                    name: { 'pt-BR': '', en: '' },
                    kind: 'group',
                    block: 'result',
                    numeratorId: null,
                    denominatorId: null,
                    sortOrder: (Math.max(0, ...nodes.map((n) => n.sortOrder)) + 10) % 10000 || 10,
                    lineIds: [],
                    termIds: [],
                  },
                });
              }}
            >
              {t('fpaPl.structure.add')}
            </Button>
          )
        }
      >
        <p className="text-sm text-muted">{t('fpaPl.structure.hint')}</p>
      </ListToolbar>
      <Table
        head={[
          t('fpaPl.code'),
          t('fpaPl.name'),
          t('fpaPl.structure.kind'),
          t('fpaPl.structure.composition'),
          ...(manage ? [''] : []),
        ]}
      >
        {nodes.length === 0 && (
          <EmptyRow colSpan={manage ? 5 : 4}>{t('fpaPl.structure.empty')}</EmptyRow>
        )}
        {orderedNodes(nodes).map(({ node: n, depth }) => (
          <tr key={n.id}>
            <td className="font-mono text-xs">{n.code}</td>
            <td className={n.kind === 'group' ? '' : 'font-semibold'}>
              <span style={{ paddingLeft: `${depth * 1.25}rem` }}>{localized(n.name)}</span>
              {n.block === 'outside' && (
                <span className="ml-2">
                  <Badge>{t('fpaPl.blocks.outside')}</Badge>
                </span>
              )}
            </td>
            <td>
              <Badge tone={KIND_TONE[n.kind]}>{t(`fpaPl.structure.kinds.${n.kind}`)}</Badge>
            </td>
            <td className="font-mono text-xs">
              {composition(n)}
              {!manage && (
                <span className="ml-2 inline-flex align-middle">
                  <MasterDeleteButton table="fpa.pl_node" id={n.id} compact />
                </span>
              )}
            </td>
            {manage && (
              <td className="text-right whitespace-nowrap">
                <span className="flex justify-end gap-2">
                  <Button
                    variant="secondary"
                    onClick={() => {
                      save.reset();
                      const { id, ...form } = n;
                      setEdit({ id, form });
                    }}
                  >
                    {t('common.edit')}
                  </Button>
                  <Button variant="secondary" onClick={() => setRemoving(n.id)}>
                    {t('fpaPl.remove')}
                  </Button>
                  <MasterDeleteButton table="fpa.pl_node" id={n.id} compact />
                </span>
              </td>
            )}
          </tr>
        ))}
      </Table>

      <FormModal
        open={!!edit}
        size="lg"
        title={t(edit?.id ? 'fpaPl.structure.edit' : 'fpaPl.structure.add')}
        description={t('fpaPl.structure.formHint')}
        dirty={!!edit}
        saving={save.isPending}
        error={save.error}
        onSave={() => edit && save.mutate(edit)}
        onClose={() => setEdit(null)}
      >
        {f && edit && (
          <>
            <FormSection columns={2}>
              <Field label={t('fpaPl.structure.kind')} required>
                <Select
                  disabled={!!edit.id}
                  value={f.kind}
                  onChange={(ev) => set({ kind: ev.target.value as PlNodeKind })}
                >
                  {(['group', 'subtotal', 'indicator'] as const).map((k) => (
                    <option key={k} value={k}>
                      {t(`fpaPl.structure.kinds.${k}`)}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field
                label={t('fpaPl.code')}
                hint={t('fpaPl.structure.codeHint')}
                error={issueFor(save.error, 'code')}
                required
              >
                <Input
                  required
                  className="font-mono"
                  maxLength={20}
                  value={f.code}
                  onChange={(ev) => set({ code: ev.target.value })}
                />
              </Field>
              <Field label={t('fpaPl.structure.namePt')} required>
                <Input
                  required
                  value={f.name['pt-BR']}
                  onChange={(ev) => set({ name: { ...f.name, 'pt-BR': ev.target.value } })}
                />
              </Field>
              <Field label={t('fpaPl.structure.nameEn')}>
                <Input
                  value={f.name.en}
                  onChange={(ev) => set({ name: { ...f.name, en: ev.target.value } })}
                />
              </Field>
              <Field label={t('fpaPl.structure.order')} hint={t('fpaPl.structure.orderHint')}>
                <Input
                  type="number"
                  min={1}
                  max={9999}
                  value={f.sortOrder}
                  onChange={(ev) => set({ sortOrder: Number(ev.target.value) || 1 })}
                />
              </Field>
              {f.kind === 'group' && (
                <>
                  <Field label={t('fpaPl.block')}>
                    <Select
                      value={f.block}
                      onChange={(ev) =>
                        set({
                          block: ev.target.value as NodeForm['block'],
                          parentId: null,
                          lineIds: [],
                        })
                      }
                    >
                      <option value="result">{t('fpaPl.blocks.result')}</option>
                      <option value="outside">{t('fpaPl.blocks.outside')}</option>
                    </Select>
                  </Field>
                  <Field label={t('fpaPl.structure.parent')} hint={t('fpaPl.structure.parentHint')}>
                    <Select
                      value={f.parentId ?? ''}
                      onChange={(ev) => set({ parentId: ev.target.value || null })}
                    >
                      <option value="">{t('fpaPl.structure.noParent')}</option>
                      {nodes
                        .filter(
                          (n) => n.kind === 'group' && n.block === f.block && n.id !== edit.id,
                        )
                        .map((n) => (
                          <option key={n.id} value={n.id}>
                            {n.code} · {localized(n.name)}
                          </option>
                        ))}
                    </Select>
                  </Field>
                </>
              )}
              {f.kind === 'indicator' && (
                <>
                  <Field label={t('fpaPl.structure.numerator')} required>
                    <Select
                      required
                      value={f.numeratorId ?? ''}
                      onChange={(ev) => set({ numeratorId: ev.target.value || null })}
                    >
                      <option value="">{t('common.choose')}</option>
                      {resultNodes.map((n) => (
                        <option key={n.id} value={n.id}>
                          {n.code} · {localized(n.name)}
                        </option>
                      ))}
                    </Select>
                  </Field>
                  <Field
                    label={t('fpaPl.structure.denominator')}
                    hint={t('fpaPl.structure.denominatorHint')}
                    required
                  >
                    <Select
                      required
                      value={f.denominatorId ?? ''}
                      onChange={(ev) => set({ denominatorId: ev.target.value || null })}
                    >
                      <option value="">{t('common.choose')}</option>
                      {resultNodes.map((n) => (
                        <option key={n.id} value={n.id}>
                          {n.code} · {localized(n.name)}
                        </option>
                      ))}
                    </Select>
                  </Field>
                </>
              )}
            </FormSection>
            {f.kind === 'subtotal' && (
              <FormSection title={t('fpaPl.structure.terms')}>
                <p className="text-sm text-muted">{t('fpaPl.structure.termsHint')}</p>
                <div className="grid grid-cols-1 gap-1 sm:grid-cols-2">
                  {resultNodes.map((n) => (
                    <label key={n.id} className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={f.termIds.includes(n.id)}
                        onChange={() => set({ termIds: toggle(f.termIds, n.id) })}
                      />
                      <span className="font-mono text-xs">{n.code}</span> {localized(n.name)}
                    </label>
                  ))}
                </div>
              </FormSection>
            )}
            {f.kind === 'group' && (
              <FormSection title={t('fpaPl.structure.lines')}>
                <p className="text-sm text-muted">{t('fpaPl.structure.linesHint')}</p>
                {sections
                  .filter((s) => !s.isSubtotal && s.block === f.block)
                  .map((s) => {
                    const own = lines.filter((l) => l.sectionId === s.id);
                    if (!own.length) return null;
                    return (
                      <fieldset key={s.id} className="flex flex-col gap-1">
                        <legend className="text-xs font-semibold text-muted">
                          {s.code} {s.expenseTypeName}
                        </legend>
                        {own.map((l) => {
                          const other = usedBy.get(l.id);
                          return (
                            <label key={l.id} className="flex items-center gap-2 text-sm">
                              <input
                                type="checkbox"
                                disabled={!!other}
                                checked={f.lineIds.includes(l.id)}
                                onChange={() => set({ lineIds: toggle(f.lineIds, l.id) })}
                              />
                              <span className="font-mono text-xs">{l.code}</span>
                              {l.classificationName}
                              {other && (
                                <span className="text-xs text-muted">
                                  ({t('fpaPl.structure.inGroup', { code: other.code })})
                                </span>
                              )}
                            </label>
                          );
                        })}
                      </fieldset>
                    );
                  })}
              </FormSection>
            )}
          </>
        )}
      </FormModal>
      <ConfirmDialog
        open={!!removing}
        title={t('fpaPl.removeTitle')}
        description={t('fpaPl.structure.removeText')}
        confirmLabel={t('fpaPl.remove')}
        tone="danger"
        busy={remove.isPending}
        error={remove.error}
        onConfirm={() => removing && remove.mutate(removing)}
        onCancel={() => setRemoving(null)}
      />
    </>
  );
}
